// Numbers and lists only: no prose, no colours.
//
// Every series here is a query result against the Alister database, verified on
// 21 August 2026. The queries live in the report bundle; the counts and windows
// each figure rests on are stated in `meta` and repeated in the method block at
// the foot of the post. When this file is regenerated, the sentences in
// post.tsx that restate a number have to be rewritten with it.

export const SNAPSHOT = {
  meta: {
    publication: "Alister Tech Talent Market Snapshot",
    edition: "No. 01",
    published: "20 August 2026",
    verifiedOn: "2026-08-21",
    // career side
    czEmployersTracked: 1327,
    czEmployersMeasured: 320,
    trackedJobsMeasured: 29384,
    trackedJobsAll: 39836,
    completedJobs: 61855,
    // github side
    contributionRows: 22796220,
    sceneSize: 8850,
    nonForkRepos: 207771
  },

  /* ── 1 · what companies did with their tech team ──────────────────────
     Window Jul 2025 to Jun 2026, employers with 30 or more tracked people.
     Buckets are applied in order, so an employer that shed under 10% lands in
     "Barely moved" even if it hired hard. */
  companySplit: {
    label: "SHARE OF COMPANIES BY WHAT THEY DID WITH THEIR TECH TEAM",
    note: "320 companies · 29,384 tracked jobs",
    buckets: [
      { key: "grew", label: "Grew", firms: 33, pct: 10, side: "up", tone: "blue" },
      { key: "replaced", label: "Replaced", firms: 46, pct: 14, side: "mid", tone: "amber" },
      { key: "still", label: "Barely moved", firms: 147, pct: 46, side: "mid", tone: "neutral" },
      { key: "shrank", label: "Shrank", firms: 94, pct: 29, side: "down", tone: "red" }
    ],
    funnel: [
      { value: "1,327", note: "tracked companies" },
      { value: "320", note: "with 30+ people, measured" },
      { value: "74%", note: "of all tracked developers" }
    ]
  },

  /* ── 2 · how long a job lasts before someone leaves ───────────────────
     Median months from joining to leaving, by year of exit, on 61,855
     completed jobs. 2026 is a partial year. */
  exitMonths: {
    label: "MONTHS IN A JOB BEFORE LEAVING, BY YEAR OF LEAVING",
    flatUntil: "2024",
    partialFrom: "2026",
    series: [
      { year: "2015", value: 24 }, { year: "2016", value: 23 },
      { year: "2017", value: 22 }, { year: "2018", value: 22 },
      { year: "2019", value: 23 }, { year: "2020", value: 23 },
      { year: "2021", value: 26 }, { year: "2022", value: 26 },
      { year: "2023", value: 25 }, { year: "2024", value: 27 },
      { year: "2025", value: 31 }, { year: "2026", value: 33 }
    ]
  },

  /* ── 3 · the language mix in public work ──────────────────────────────
     Share of active public non-fork repositories by primary language, grouped
     by the year of last push. TypeScript passes Java in 2022. */
  languages: {
    label: "SHARE OF ACTIVE PUBLIC REPOSITORIES BY MAIN LANGUAGE",
    crossover: "2022",
    years: ["2018", "2019", "2020", "2021", "2022", "2023", "2024", "2025"],
    series: [
      { key: "typescript", label: "TypeScript", tone: "blue", weight: 1,
        values: [3.1, 2.7, 3.3, 6.2, 8.5, 12.2, 11.9, 13.9] },
      { key: "python", label: "Python", tone: "blue", weight: 0.5,
        values: [11.9, 13.7, 15.3, 15.2, 15.1, 14.1, 15.7, 18.1] },
      { key: "javascript", label: "JavaScript", tone: "red", weight: 1,
        values: [16.3, 12.5, 11.8, 16.3, 17.7, 17.8, 13.5, 10.4] },
      { key: "java", label: "Java", tone: "red", weight: 0.62,
        values: [12.1, 10.5, 8.6, 8.1, 7.4, 6.5, 6.7, 5.4] },
      { key: "php", label: "PHP", tone: "red", weight: 0.36,
        values: [6.5, 6.0, 4.4, 3.9, 3.4, 3.5, 3.2, 3.0] }
    ]
  },

  /* ── 4 · what the open source scene ships ─────────────────────────────
     Quarterly contributions among a fixed group of 8,850 profiles with 100 or
     more public contributions over Jul 2025 to Jun 2026. */
  scene: {
    label: "PUBLIC CONTRIBUTIONS PER QUARTER · MEDIAN AND THE MIDDLE HALF",
    quarters: ["Q3 '25", "Q4 '25", "Q1 '26", "Q2 '26"],
    p25: [16, 28, 42, 42],
    median: [70, 82, 111, 119],
    p75: [183, 200, 269, 302]
  },

  /* ── 5 · the AI corner of public work ─────────────────────────────────
     Keyword detection over names, descriptions and summaries, so the shares
     are a floor. 2026 excluded as partial. */
  ai: {
    label: "AI AND ML SHARE OF REPOSITORIES AGAINST SHARE OF STARS",
    trendLabel: "AND IT HAS MORE THAN DOUBLED SINCE 2021",
    years: ["2019", "2020", "2021", "2022", "2023", "2024", "2025"],
    share: [2.7, 2.9, 2.2, 2.2, 2.6, 3.6, 4.7],
    prominence: [
      { key: "repos", label: "Share of active repos", value: 3.7, tone: "neutral" },
      { key: "stars", label: "Share of all stars", value: 9.3, tone: "amber" }
    ],
    avgStars: { aiml: 13.8, other: 5.1 }
  },

  /* ── 6 · juniors as a share of the active pool ────────────────────────
     A share rather than a count, so thinner coverage in the early years
     shrinks numerator and denominator together. 2026 is partial. */
  juniorShare: {
    label: "SHARE OF ACTIVE DEVELOPERS WITHIN THREE YEARS OF THEIR FIRST JOB",
    reference: { value: 26, label: "A QUARTER OF THE WORKFORCE" },
    partialFrom: "2026",
    years: ["2021", "2022", "2023", "2024", "2025", "2026"],
    values: [26, 25, 23, 21, 16, 12],
    jrTitleShare: 2.0
  }
} as const;
